import { createAuthRequestInit } from '../utils/auth';
import { API_BASE } from '../config/api';

export type ApiUserRole = 'demo' | 'user' | 'manager' | 'admin' | 'super_admin';

export interface ApiUser {
  id: string;
  firstName: string;
  lastName: string;
  companyEmail: string;
  contactNumber: string;
  department?: string | null;
  company?: string | null;
  role: ApiUserRole;
  lastLogin?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiRolePermission {
  module: string;
  canRead: boolean;
  canWrite: boolean;
}

export interface ApiUserResponse {
  user: ApiUser;
  permissions: ApiRolePermission[];
}

export async function getUsers(): Promise<ApiUser[]> {
  const res = await fetch(`${API_BASE}/users`, createAuthRequestInit());
  if (!res.ok) {
    throw new Error('Failed to fetch users');
  }
  const json = await res.json();
  return json.data.users as ApiUser[];
}

export async function getUser(id: string): Promise<ApiUserResponse> {
  const url = `${API_BASE}/users/${encodeURIComponent(id)}`;
  const res = await fetch(url, createAuthRequestInit());
  if (!res.ok) {
    const text = await res.text().catch(() => null);
    throw new Error(text || `Failed to fetch user (${res.status})`);
  }
  const json = await res.json();
  return {
    user: json.data.user as ApiUser,
    permissions: (json.data.permissions as ApiRolePermission[]) ?? []
  };
}

export async function createUser(payload: {
  firstName: string;
  lastName: string;
  companyEmail: string;
  contactNumber: string;
  department?: string;
  company?: string;
  role: ApiUserRole;
  temporaryPassword?: string;
}): Promise<ApiUserResponse> {
  const res = await fetch(`${API_BASE}/users`, createAuthRequestInit({
    method: 'POST',
    body: JSON.stringify(payload)
  }, true));
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to create user');
  }
  const json = await res.json();
  return {
    user: json.data.user as ApiUser,
    permissions: (json.data.permissions as ApiRolePermission[]) ?? []
  };
}

export async function setTemporaryPassword(userId: string, temporaryPassword: string): Promise<void> {
  const res = await fetch(`${API_BASE}/users/${encodeURIComponent(userId)}/temporary-password`, createAuthRequestInit({
    method: 'POST',
    body: JSON.stringify({ temporaryPassword })
  }, true));
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to set temporary password');
  }
}

export function mapRoleLabelToApi(roleLabel: string): ApiUserRole {
  switch (roleLabel) {
    case 'Super Admin':
      return 'super_admin';
    case 'Admin':
      return 'admin';
    case 'Manager':
      return 'manager';
    case 'Demo':
      return 'demo';
    case 'User':
    default:
      return 'user';
  }
}

export function mapApiRoleToLabel(role: ApiUserRole): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'admin':
      return 'Admin';
    case 'manager':
      return 'Manager';
    case 'demo':
      return 'Demo';
    case 'user':
    default:
      return 'User';
  }
}

export async function updateUser(id: string, payload: {
  firstName?: string;
  lastName?: string;
  companyEmail?: string;
  contactNumber?: string;
  department?: string | null;
  company?: string | null;
  role?: ApiUserRole;
}): Promise<ApiUserResponse> {
  const res = await fetch(`${API_BASE}/users/${id}`, createAuthRequestInit({
    method: 'PUT',
    body: JSON.stringify(payload)
  }, true));
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to update user');
  }
  const json = await res.json();
  return {
    user: json.data.user as ApiUser,
    permissions: (json.data.permissions as ApiRolePermission[]) ?? []
  };
}

export async function deleteUser(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/users/${encodeURIComponent(id)}`, createAuthRequestInit({
    method: 'DELETE'
  }));
  if (!res.ok && res.status !== 204) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to delete user');
  }
}
